
import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid, TextField, Typography, Chip, CircularProgress } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import PrintIcon from '@mui/icons-material/Print';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { addYears } from "date-fns";
import { useEffect, useMemo, useReducer } from "react";
import useDialogModal from "../../../../../hooks/useDialogModal";
import { calculateItemTotal, formatAmountForDisplay, getCurrentUTCDate, niceDate } from "../../../../../utils";
import validLookup from "../../../../../utils/validLookup";
import { SelectLocation } from "../../../../common";
import AppDialogTitle from "../../../../ui/appDialogTitle";
import InvoiceButtonComponent, { ButtonType } from "./invoiceButtonComponents";
import TreatmentSelectDialog from "../../../../admin/ValidData/validTreatment/treatmentSelectDialog";
import AccessorySelectDialog from "../../../../admin/inventory/accessories/accessorySelectDialog";
import InvoiceItemsTable from "./invoiceItemsTable";
import PaymentCollector from "./paymentCollector";
import DiscountPromotion from "./discountAndPromotion";
import InsuranceClaimCollector from "./insuranceClaimCollector";
import RefundCollector from "./refundCollector";
import CreditMemoCollector from "./creditMemoCollector";
import { useQuery } from "react-query";
import { ValidDataService } from "../../../../../service/validDataService";
import InvoiceStatusSelect from "./invoiceStatusSelect";
import { InvoiceService } from "../../../../../service/invoiceService";
import CreditMemoService from "../../../../../service/creditMemoService";
import { LabOrderService } from "../../../../../service/labOrderService";
import { isLocalDev } from "../../../../../utils/environment";
import { toast } from "react-toastify";
import CustomerInfobar from "../../../customerInfobar";
import ConfirmationDialog from "../../../../ui/confirmationDialog";
// EditGlassesDialog removed - now using NewGlassesDialog for both add and edit
import EditContactsDialog from "./editContactsDialog";
import NewGlassesDialog from "./newGlassesDialog";
import NewContactsDialog from "./newContactsDialog";
import ContactLensConfigurationDialog from "./ContactLensConfigurationDialog";
import ServiceSelectDialog from "../../../../admin/ValidData/validServices/serviceSelectDialog";
import LabOrderDialog from "../../labOrder/labOrderDialog";
import TransferInvoiceDialog from "./TransferInvoiceDialog";
import InvoiceTransferTrail from "./InvoiceTransferTrail";
import { useInvoiceSMS } from '../../../../../hooks/useInvoiceSMS';
import { v4 as uuidv4 } from 'uuid'
import useInvoiceCalculations from "../../../../../hooks/useInvoiceCalculations";
import useInvoiceState from "../../../../../hooks/useInvoiceState";
import FrameSelectDialog from "../../../../admin/inventory/frames/frameSelectDialog";
// LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow
import { usePrint } from "../../../../../hooks/usePrint";
import AddTaskDialog from "../../../../task/AddTaskDialog";
import { useCan } from "hooks/useCan";
import Can, { HideIf } from "components/auth/Can";

export default function EditInvoiceDialog({ onClose, open, customer, invoiceData, onRefresh }) {
    const can = useCan();
    const { data: invoiceStatusType, isLoading: invoivceStatusTypeLoading, error: lensTypeError } = useQuery('invoiceStatusTypes', ValidDataService.getInvoiceStatusTypes)

    const { data: invoiceItems, isLoading: invoiceItemsLoading, error: invoiceItemsError } = useQuery('invoiceItems', () => InvoiceService.getInvoiceItems(invoiceData.id), {
        enabled: !!invoiceData
    })

    // const { data: refundsData, isLoading: refundsLoading } = useQuery(['refunds', invoiceData.id], () => InvoiceService.getRefundsByInvoiceId(invoiceData.id), {
    //     enabled: !!invoiceData.id
    // })

    // const { data: creditMemosData, isLoading: creditMemosLoading } = useQuery(['creditMemos', invoiceData.id], () => CreditMemoService.getCreditMemosByInvoiceId(invoiceData.id), {
    //     enabled: !!invoiceData.id
    // })

    const {
        invoiceState: state,
        dispatch,
        actions
    } = useInvoiceState({
        id: invoiceData.id,
        invoiceNumber: invoiceData.invoiceNumber,
        selectService: false,
        selectTreatment: false,
        selectAccessory: false,
        selectFrame: false,
        selectLens: false,
        selectGlasses: false,
        selectContacts: false,
        selectConfiguredContactLens: false,
        editGlasses: false,
        editContacts: false,
        editFrame: false,
        editingFrameItem: null,        
        labOrderDialog: false,
        glassesToEdit: null,
        contactsToEdit: null,
        inProgress: false,
        location: invoiceData.location,
        notes: invoiceData.notes,
        tray: invoiceData.tray,
        associate: invoiceData.associate,
        invoiceItems: [],
        glassesOrdersForLab: [],
        payments: [],
        discounts: [],
        claims: [],
        refunds: [],
        creditMemos: [],
        status: invoiceData.status,
        invoiceDate: invoiceData.invoiceDate ? invoiceData.invoiceDate : getCurrentUTCDate(),
        endDate: addYears(Date.now(), 1),
        tabIndex: 0,
        errors: {},
        showTaskDialog: false,
    });

    // SMS functionality
    const { isSending, sendSMS, smsError } = useInvoiceSMS();

    const { printPDF, printSimpleHTML, isGenerating: isPrinting, cleanup } = usePrint();

    useEffect(() => {
        if (!open) {
            cleanup();
        }
    }, [open, cleanup]);

    useEffect(() => () => cleanup(), [cleanup]);


    useEffect(() => {
        if (invoiceItems && invoiceItems.data) {
            actions.setItems( invoiceItems.data.items );
            actions.setPayments( invoiceItems.data.payments );
            actions.setDiscounts( invoiceItems.data.discounts );
            actions.setClaims( invoiceItems.data.insuranceClaims );
            actions.setRefunds( invoiceItems.data.refunds );
            actions.setCreditMemos( invoiceItems.data.creditMemos );
            // leave out for now
            //  Convert existing lab orders to glasses orders format for consistency
            // const existingGlassesOrdersForLab = (invoiceItems.data.labOrders || []).map(labOrder => ({
            //     groupId: labOrder.groupId,
            //     rxId: labOrder.rx?.id,
            //     labId: labOrder.labId || labOrder.lab?.id,
            //     labInstructions: labOrder.labInstructions,
            //     orderDate: labOrder.orderDate,
            //     promiseDate: labOrder.dueDate,
            //     rushOrder: labOrder.rushOrder || false,
            //     orderType: 'glasses',
            //     // Keep original lab order data for editing
            //     //lab: labOrder.lab,
            //     rx: labOrder.rx,
            //     frameHandling: labOrder.frameHandling,
            // }));
            //actions.setInvoiceMeta({ glassesOrdersForLab: existingGlassesOrdersForLab });
        }

    }, [invoiceItems]);

    // old way
    // useEffect(() => {
    //     if (refundsData && refundsData.data) {
    //         actions.setInvoiceMeta({ refunds: refundsData.data });
    //     }
    // }, [refundsData]);

    // old way
    // useEffect(() => {
    //     if (creditMemosData && creditMemosData.data) {
    //         actions.setInvoiceMeta({ creditMemos: creditMemosData.data });
    //     }
    // }, [creditMemosData]);


    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    // calculations
    const { lineItemsSubtotal, totalDiscount, totalTax, totalDue, totalCollected, balanceDue, paymentStatus, totalItemTaxAmount, totalItemDiscountAmount, totalOverallDiscountAmount,
        taxOnDiscountedAmount,
        totalCustomerPaid,
        totalInsuranceApproved,
        totalRefunded,
        maxRefundAmount } = useInvoiceCalculations(state);

    const wasClosedInitially = useMemo(() =>
        invoiceData.status === 'closed' || invoiceData.status === 'refund',
        [invoiceData.status]
    );
    const isQuote = useMemo(() => state.status === 'quote', [state.status]);


    // validate before saving
    const validate = () => {
        const newErrors = {};
        if (!state.location) newErrors.location = 'Location is required';
        if (!state.associate) newErrors.associate = 'Associate is required';
        if (!state.tray) newErrors.tray = 'Tray is required';

        actions.setInvoiceMeta({ type: 'SET_ERRORS', errors: newErrors });
        return Object.keys(newErrors).length === 0;
    }

    const handleSendTextNotification = async () => {
        try {

            //TODO this needs to come from settings
            const message = `Your order is ready for pickup. Invoice #${state.invoiceNumber}. Please visit us at your convenience.`;

            const response = await sendSMS(message, customer.customer, state.id);

            if (response.code === 0) {
                toast.success('Text notification sent successfully');
            } else {
                toast.error(response.message || 'Failed to send notification');
            }
        } catch (error) {
            toast.error('Error sending text notification');
        }
    };

    const handlePrintInvoice = async () => {
        if (!state.invoiceNumber) {
            toast.error('Please save the invoice before printing');
            return;
        }

        if (!state.id) {
            toast.error('Invoice not ready for printing');
            return;
        }

        try {
            await printPDF(() => InvoiceService.print(state.id));
        } catch (error) {
            if (isLocalDev) {
                console.error('Error printing invoice:', error);
            }
            toast.error('Failed to print invoice');
        }
    };

    const handleTestPrintInvoice = async () => {
        try {
            console.log('Test print - simple HTML (no server call)');
            printSimpleHTML();
        } catch (error) {
            if (isLocalDev) {
                console.error('Error printing test invoice:', error);
            }
            toast.error('Failed to print test invoice');
        }
    };

    const handleConvertToInvoice = async () => {
        if (state.status !== 'quote') {
            toast.error('Only quotes can be converted to invoices');
            return;
        }

        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.convertQuoteToInvoice(state.id);
            actions.setInvoiceMeta({ inProgress: false });
            const { code, message, data } = response;

            if (code !== 0) {
                console.error('Error converting quote to invoice:', message);
                toast.error(message);
                return;
            }

            // Update the state with the new invoice details
            actions.setInvoiceMeta({
                invoiceNumber: data.invoiceNumber,
                status: data.status
            });
            toast.success(message);

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error converting quote to invoice:', error);
            }
            toast.error('Error converting quote to invoice');
        }
    };

    const handleTransferSuccess = async (transferredInvoice) => {
        // update the state with the new invoice details
        actions.setInvoiceMeta({
            id: transferredInvoice.id,
            invoiceNumber: transferredInvoice.invoiceNumber,
            status: transferredInvoice.status,
            notes: transferredInvoice.notes,
            tray: '',
            associate: '',
            location: transferredInvoice.location,
            invoiceDate: getCurrentUTCDate(),
            invoiceItems: transferredInvoice.items,
            //glassesOrdersForLab: transferredInvoice.glassesOrdersForLab,
            payments: transferredInvoice.payments,
            discounts: transferredInvoice.discounts,
            claims: transferredInvoice.claims,
            refunds: [],
            creditMemos: [],
            showTransferDialog: false
        });
    }

    const handleTransferInvoice = async () => {
        if (state.status === 'transferred') {
            toast.error('This invoice has already been transferred');
            return;
        }

        if (['cancelled', 'voided', 'refunded'].includes(state.status)) {
            toast.error(`Cannot transfer invoice with status: ${state.status}`);
            return;
        }

        // Show the transfer options dialog
        actions.setInvoiceMeta({ showTransferDialog: true });
    };

    const handleAddTask = () => {
        actions.setInvoiceMeta({ showTaskDialog: true });
    };

    /**
     * Handle navigation to a different invoice from the transfer trail
     * This allows users to click on invoice numbers in the transfer history
     */
    const handleInvoiceNavigation = (selectedInvoice) => {
        if (selectedInvoice && selectedInvoice.id !== invoiceData.id) {
            // TODO: load the selected invoice

        }
    };

    const handleSave = async () => {
        if (wasClosedInitially) {
            toast.warn('This invoice is closed/refunded and cannot be modified.');
            return;
        }
        if (!validate()) {
            console.log('Validation failed');
            return;
        }

        // Clean up invoice items before sending to backend
        const processedInvoiceItems = state.invoiceItems.map(item => {
            const itemForSave = { ...item };
            
            // Clean up: Remove UI-only fields before sending to backend
            delete itemForSave.inventoryItem; // Large nested object, not needed by backend
            delete itemForSave.rx; // Only send rxId, not the full rx object
            
            // Clean up itemConfig: keep IDs and snapshot data (names for display)
            if (itemForSave.itemConfig && itemForSave.type === ButtonType.LENS) {
                const cleanConfig = {
                    // IDs for referential integrity
                    manufacturerId: itemForSave.itemConfig.manufacturerId,
                    lensModelId: itemForSave.itemConfig.lensModelId,
                    lensMaterialId: itemForSave.itemConfig.lensMaterialId,
                    lensIndexId: itemForSave.itemConfig.lensIndexId,
                    treatmentIds: itemForSave.itemConfig.treatmentIds || [],
                    
                    // Snapshot data for display (no DB lookups needed on read)
                    manufacturer: itemForSave.itemConfig.manufacturer ? {
                        id: itemForSave.itemConfig.manufacturer.id,
                        name: itemForSave.itemConfig.manufacturer.name
                    } : null,
                    model: itemForSave.itemConfig.model ? {
                        id: itemForSave.itemConfig.model.id,
                        modelName: itemForSave.itemConfig.model.modelName,
                        lensType: itemForSave.itemConfig.model.lensType
                    } : null,
                    material: itemForSave.itemConfig.material ? {
                        id: itemForSave.itemConfig.material.id,
                        name: itemForSave.itemConfig.material.name
                    } : null,
                    index: itemForSave.itemConfig.index ? {
                        id: itemForSave.itemConfig.index.id,
                        indexValue: itemForSave.itemConfig.index.indexValue
                    } : null,
                    treatments: (itemForSave.itemConfig.treatments || []).map(t => ({
                        id: t.id,
                        name: t.name,
                        cost: t.cost,
                        listPrice: t.listPrice
                    }))
                };
                itemForSave.itemConfig = cleanConfig;
            }
            
            return itemForSave;
        });

        // Gather all necessary data from state
        // Include ALL items (even those marked for deletion) - backend will handle deletions
        const invoiceDataToSave = {
            // If editing, include rowid
            id: state.id, // Pass the original ID if editing
            invoiceNumber: state.invoiceNumber,
            customerId: customer.customer.id, // Assuming this is the customer ID
            associate: state.associate,
            creditMemos: state.creditMemos,
            tray: state.tray,
            invoiceItems: processedInvoiceItems, // Use cleaned items
            glassesOrdersForLab: state.glassesOrdersForLab || [],
            totalAmount: totalDue,
            paidAmount: totalCustomerPaid,
            balanceAmount: balanceDue,
            taxAmount: totalItemTaxAmount,
            discountAmount: totalOverallDiscountAmount,
            payments: state.payments, // Includes payments with _markedForDeletion flag
            discounts: state.discounts, // Includes discounts with _markedForDeletion flag
            claims: state.claims, // Includes claims with _markedForDeletion flag
            refunds: state.refunds, // Includes refunds with _markedForDeletion flag
            location: state.location,
            status: state.status, // Include the current status
            notes: state.notes, // Add notes if needed
            // Add Tray and Associate values (need to add state for these TextFields)
            // tray: state.tray,
            // associate: state.associate,
            invoiceDate: state.invoiceDate, // Or from state if added
            // Include formik values if they represent the core RX data for the order
            // rxData: formik.values,
            // Or if RX is an item, ensure item has rxDetails property
        };

        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.update(invoiceDataToSave);
            actions.setInvoiceMeta({ inProgress: false });
            const { code, message, data } = response;

            if (code !== 0) {
                console.error('Error saving invoice:', message);
                toast.error(message);
                return;
            }
            // update invoice number in the state
            actions.setInvoiceMeta({ invoiceNumber: data.invoiceNumber, id: data.id });
            toast.success(message);

            actions.setItems( data.items );
            actions.setPayments( data.payments );
            actions.setDiscounts( data.discounts );
            actions.setClaims( data.insuranceClaims );
            actions.setRefunds( data.refunds );
            actions.setCreditMemos( data.creditMemos );

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error saving invoice:', error);
            }
            //toast.error(error.message ? error.message : 'Error saving invoice');
        }
    }

    const handleAssociateChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ associate: inputValue });
    }

    const handleTrayChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ tray: inputValue });
    }

    const handleStatusChange = (newStatus) => {
        // Warn if changing to cancelled/refunded with outstanding balance
        if (['cancelled', 'refunded', 'void', 'voided'].includes(newStatus) && balanceDue > 0) {
            showConfirmationDialog((action) => {
                if (action === validLookup.confirmationDialogAction.ok) {
                    actions.setInvoiceMeta({ status: newStatus });
                }
                closeConfirmationDialog();
            }, {
                title: 'Outstanding Balance',
                message: `This invoice has an outstanding balance of $${balanceDue.toFixed(2)}. Changing status to "${newStatus}" will set the total and balance to $0.00. Continue?`,
            });
        } else {
            actions.setInvoiceMeta({ status: newStatus });
        }
    }

    const handleButtonClick = (buttonType) => {
        switch (buttonType) {
            case ButtonType.NEW_GLASSES:
                actions.setInvoiceMeta({ selectGlasses: true });
                break;  
            case ButtonType.CONTACT_LENS:
                actions.setInvoiceMeta({ selectConfiguredContactLens: true });
                break;
            case ButtonType.FRAME:
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            // Note: ButtonType.LENS removed - lens configuration is now part of NEW_GLASSES workflow
            case ButtonType.SERVICES:
                actions.setInvoiceMeta({ selectService: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            case ButtonType.SEND_TO_LAB:
                actions.setInvoiceMeta({ labOrderDialog: true });
                break;
            default:
                break;
        }
    }

    const handleEditItem = (item) => {
        // show the editglasses dialog only if item type is glasses
        if (item.groupId && (item.type === ButtonType.FRAME || item.type === ButtonType.LENS)) {
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];

            // Transform grouped items into structure expected by NewGlassesDialog
            const frameItem = groupItems.find(i => i.type === 'frame');
            const odLens = groupItems.find(i => i.type === 'lens' && i.eye === 'OD');
            const osLens = groupItems.find(i => i.type === 'lens' && i.eye === 'OS');
            
            // Get Rx from either lens (they should have the same rxId)
            const rx = odLens?.rx || osLens?.rx;

            const glassesData = {
                items: groupItems,
                groupId: item.groupId,
                // Use flat frame structure from backend (no more inventoryItem wrapper)
                frame: frameItem?.frame || frameItem, // Prefer normalized frame from backend
                odLens,
                osLens,
                rx
            };

            actions.setInvoiceMeta({ editGlasses: true, glassesToEdit: glassesData });
        }
        // show the editcontacts dialog only if item type is contacts
        else if (item.type === ButtonType.CONTACT_LENS) {
            
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];
            const OD = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OD');
            const OS = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OS');
            
            actions.setInvoiceMeta({ editContacts: true, contactsToEdit: { 
                items: groupItems, 
                OD, 
                OS, 
                rx: item.rx,
                groupId: item.groupId } });
        } else if (item.type === ButtonType.FRAME) {
            // Direct frame item edit - open frame dialog for replacement
            actions.setInvoiceMeta({ editFrame: true, editingFrameItem: item });
        } 
    };

    const handleDeleteItem = (item) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // For new items (with tempId), just remove from state
                if (item.tempId && !item.id) {
                    actions.removeItem(item);
                } else {
                    // For existing items, mark for deletion
                    actions.markItemForDeletion(item.id);
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark item for deletion? It will be removed when you save the invoice.`,
        });
    };

    const handleReplaceFrame = (newFrame) => {
        // Replace the existing frame item with the newly selected frame
        if (state.editingFrameItem) {
            const updatedFrameItem = {
                ...state.editingFrameItem,
                itemId: newFrame.id,
                modelNumber: newFrame.modelNumber,
                brand: newFrame.brand || null,
                description: newFrame.name || newFrame.modelNumber || newFrame.description || 'Frame',
                price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                tax: newFrame.defaultTaxPercentage || 0,
                total: calculateItemTotal({
                    price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                    quantity: state.editingFrameItem.quantity || 1,
                    tax: newFrame.defaultTaxPercentage || 0,
                    discount: state.editingFrameItem.discount || 0
                }),
                properties: newFrame.properties || {}
            };
            actions.updateItem({ item: state.editingFrameItem, updatedItem: updatedFrameItem });
            actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null });
        }
    };

    // Add this handler at the component level
    const handleItemUpdate = (item, field, value) => {
        actions.updateItem({ item, field, value });
    };

    /*
        called after glass order is edited and closed
    */
    const handleUpdateGlasses = (updatedGlasses) => {
        const glassesToEdit = state.glassesToEdit;
        if (glassesToEdit && glassesToEdit.items) {
            // Update frame item
            const frameItem = glassesToEdit.items.find(item => item.type === 'frame' && item.groupId === updatedGlasses.groupId);

            if (frameItem) {
                const updatedFrameItem = {
                    ...frameItem,
                    itemId: updatedGlasses.frame?.itemId || frameItem.itemId,
                    description: updatedGlasses.frame?.modelNumber || frameItem.description,
                    price: updatedGlasses.frame?.listPrice || frameItem.price,
                    tax: updatedGlasses.frame?.defaultTaxPercentage || frameItem.tax,
                    frameHandling: updatedGlasses.frame.frameHandling,
                    customerSuppliedFrame: updatedGlasses.frame.customerSuppliedFrame,
                    // Store flat normalized frame for reopening
                    frame: updatedGlasses.frame.itemId ? {
                        id: updatedGlasses.frame.id,
                        itemId: updatedGlasses.frame.itemId,
                        modelNumber: updatedGlasses.frame.modelNumber,
                        brand: updatedGlasses.frame.brand,
                        supplier: updatedGlasses.frame.supplier,
                        properties: updatedGlasses.frame.properties,
                        listPrice: updatedGlasses.frame.listPrice,
                        isTaxable: updatedGlasses.frame.isTaxable,
                        defaultTaxPercentage: updatedGlasses.frame.defaultTaxPercentage,
                        location: updatedGlasses.frame.location
                    } : null,
                    total: calculateItemTotal({
                        ...frameItem,
                        price: updatedGlasses.frame?.listPrice || frameItem.price
                    })
                };
                actions.updateItem({ item: frameItem, updatedItem: updatedFrameItem });
            }

            // Update OD lens item
            const odLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OD' && item.groupId === updatedGlasses.groupId);
            if (odLensItem && updatedGlasses.lens?.OD) {
                const updatedODLensItem = {
                    ...odLensItem,
                    itemId: null, // Lenses don't reference inventory
                    description: `${updatedGlasses.lens.OD.model?.modelName || 'Lens'} - OD`,
                    price: updatedGlasses.lens.OD.pricing?.totalRetail || odLensItem.price,
                    rxId: updatedGlasses.lens.rx?.id || null, // rx is at lens level, not OD level
                    labInstructions: updatedGlasses.lens.OD.labInstructions || null,
                    itemConfig: {
                        manufacturerId: updatedGlasses.lens.OD.manufacturerId,
                        lensModelId: updatedGlasses.lens.OD.lensModelId,
                        lensMaterialId: updatedGlasses.lens.OD.lensMaterialId,
                        lensIndexId: updatedGlasses.lens.OD.lensIndexId,
                        treatmentIds: updatedGlasses.lens.OD.treatmentIds || [],
                        manufacturer: updatedGlasses.lens.OD.manufacturer || null,
                        model: updatedGlasses.lens.OD.model || null,
                        material: updatedGlasses.lens.OD.material || null,
                        index: updatedGlasses.lens.OD.index || null,
                        treatments: updatedGlasses.lens.OD.treatments || []
                    },
                    total: calculateItemTotal({
                        ...odLensItem,
                        price: updatedGlasses.lens.OD.pricing?.totalRetail || odLensItem.price
                    })
                };
                actions.updateItem({ item: odLensItem, updatedItem: updatedODLensItem });
            }

            // Update OS lens item
            const osLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OS' && item.groupId === updatedGlasses.groupId);
            if (osLensItem && updatedGlasses.lens?.OS) {
                const updatedOSLensItem = {
                    ...osLensItem,
                    itemId: null, // Lenses don't reference inventory
                    description: `${updatedGlasses.lens.OS.model?.modelName || 'Lens'} - OS`,
                    price: updatedGlasses.lens.OS.pricing?.totalRetail || osLensItem.price,
                    rxId: updatedGlasses.lens.rx?.id || null, // rx is at lens level, not OS level
                    labInstructions: updatedGlasses.lens.OS.labInstructions || null,
                    itemConfig: {
                        manufacturerId: updatedGlasses.lens.OS.manufacturerId,
                        lensModelId: updatedGlasses.lens.OS.lensModelId,
                        lensMaterialId: updatedGlasses.lens.OS.lensMaterialId,
                        lensIndexId: updatedGlasses.lens.OS.lensIndexId,
                        treatmentIds: updatedGlasses.lens.OS.treatmentIds || [],
                        manufacturer: updatedGlasses.lens.OS.manufacturer || null,
                        model: updatedGlasses.lens.OS.model || null,
                        material: updatedGlasses.lens.OS.material || null,
                        index: updatedGlasses.lens.OS.index || null,
                        treatments: updatedGlasses.lens.OS.treatments || []
                    },
                    total: calculateItemTotal({
                        ...osLensItem,
                        price: updatedGlasses.lens.OS.pricing?.totalRetail || osLensItem.price
                    })
                };
                actions.updateItem({ item: osLensItem, updatedItem: updatedOSLensItem });
            }

            // Handle treatment updates (if needed - treatments are now part of lens pricing)
            if (updatedGlasses.treatments && updatedGlasses.treatments.length > 0) {
                // Remove existing standalone treatments for this group
                const existingTreatments = glassesToEdit.items.filter(item => 
                    item.type === ButtonType.TREATMENTS && item.groupId === updatedGlasses.groupId
                );
                existingTreatments.forEach(treatment => {
                    actions.setInvoiceMeta({ type: 'REMOVE_ITEM', item: treatment });
                });

                // Add new treatments
                updatedGlasses.treatments.forEach(treatment => {
                    const treatmentItem = {
                        id: uuidv4(),
                        itemId: treatment.id,
                        description: treatment.name || 'Treatment',
                        type: ButtonType.TREATMENTS,
                        quantity: 1,
                        price: +treatment.listPrice || 0,
                        tax: treatment.defaultTaxPercentage || 0,
                        discount: 0,
                        total: calculateItemTotal({ price: +treatment.listPrice || 0, quantity: 1, tax: treatment.defaultTaxPercentage || 0, discount: 0 }),
                        date: getCurrentUTCDate(),
                        groupId: updatedGlasses.groupId
                    };
                    actions.addItem(treatmentItem);
                });
            }

            actions.setInvoiceMeta({ editGlasses: false, glassesToEdit: null });
        }
    };

    /*
        called after contacts order is edited and closed
    */
    const handleUpdateContacts = (updatedContacts) => {
        const contactsToEdit = state.contactsToEdit;
        if (contactsToEdit) {
            // Extract OD and OS data from updatedContacts.contactLens
            const od = updatedContacts.od;
            const os = updatedContacts.os;
            const topLevelRx = updatedContacts.rx;

            // Update OD contact lens item
            const odContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OD' && item.groupId === updatedContacts.groupId);
            
            if (od) {
                const updatedODItem = {
                    ...odContactItem,
                    itemId: null,
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    isTrial: od.isTrial || false,
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OD',
                    total: calculateItemTotal({
                        ...odContactItem,
                        price: od.retailPrice || 0,
                        quantity: od.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: odContactItem, updatedItem: updatedODItem });
            }

            // Update OS contact lens item
            const osContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OS' && item.groupId === updatedContacts.groupId);
            if (os) {
                const updatedOSItem = {
                    ...osContactItem,
                    itemId: null,
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    isTrial: os.isTrial || false,
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OS',
                    total: calculateItemTotal({
                        ...osContactItem,
                        price: os.retailPrice || 0,
                        quantity: os.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: osContactItem, updatedItem: updatedOSItem });
            }

            // No treatments for contacts, but if you ever add, handle here

            actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null });
        }
    }

    const addToInvoice = (type, item) => {

        // handle glasses item differently
        if (type === ButtonType.NEW_GLASSES) {
            const { frame, lens, treatments, groupId } = item
            
            // Add frame to invoice
            if (frame) {
                const frameItem = {
                    id: uuidv4(), // Generate a unique ID for this instance in the UI state
                    itemId: frame.id,
                    description: frame.name || frame.modelNumber || frame.description || 'Patient Own Frame',
                    brand: frame.brand,
                    type: ButtonType.FRAME,
                    quantity: frame.quantity || 1,
                    price: +frame.listPrice || +frame.price || +frame.cost || +frame.total || 0,
                    tax: frame.defaultTaxPercentage || 0,
                    discount: frame.discount || 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    frameHandling: frame.frameHandling,
                    inventoryItem: frame,
                    customerSuppliedFrame: frame.customerSuppliedFrame
                }
                actions.addItem(frameItem);
            }

            // Add lens items - now separate for OD and OS
            // rx is stored at lens level, not OD/OS level
            if (lens && lens.OD) {
                const odLensItem = {
                    id: uuidv4(),
                    itemId: null, // Lenses don't reference inventory, use lensConfig instead
                    description: `${lens.OD.model?.modelName || 'Lens'} - OD`,
                    type: ButtonType.LENS,
                    quantity: 1,
                    price: lens.OD.pricing?.totalRetail || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OD',
                    rxId: lens.rx?.id || null, // Extract rxId from lens.rx (shared for both eyes)
                    labInstructions: lens.OD.labInstructions || null,
                    // Store lens configuration metadata
                    itemConfig: {
                        manufacturerId: lens.OD.manufacturerId,
                        lensModelId: lens.OD.lensModelId,
                        lensMaterialId: lens.OD.lensMaterialId,
                        lensIndexId: lens.OD.lensIndexId,
                        model: lens.OD.model,
                        material: lens.OD.material,
                        index: lens.OD.index,
                        pricing: lens.OD.pricing,
                        treatmentIds: lens.OD.treatmentIds || []
                    }
                };
                actions.addItem(odLensItem);
            }

            if (lens && lens.OS) {
                const osLensItem = {
                    id: uuidv4(),
                    itemId: null, // Lenses don't reference inventory, use lensConfig instead
                    description: `${lens.OS.model?.modelName || 'Lens'} - OS`,
                    type: ButtonType.LENS,
                    quantity: 1,
                    price: lens.OS.pricing?.totalRetail || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OS',
                    rxId: lens.rx?.id || null, // Extract rxId from lens.rx (shared for both eyes)
                    labInstructions: lens.OS.labInstructions || null,
                    // Store lens configuration metadata
                    itemConfig: {
                        manufacturerId: lens.OS.manufacturerId,
                        lensModelId: lens.OS.lensModelId,
                        lensMaterialId: lens.OS.lensMaterialId,
                        lensIndexId: lens.OS.lensIndexId,
                        model: lens.OS.model,
                        material: lens.OS.material,
                        index: lens.OS.index,
                        pricing: lens.OS.pricing,
                        treatmentIds: lens.OS.treatmentIds || []
                    }
                };
                actions.addItem(osLensItem);
            }

            // Add treatments from lens configuration (now part of each eye's config)
            if (lens && lens.OD && lens.OD.treatments && lens.OD.treatments.length > 0) {
                lens.OD.treatments.forEach(treatment => {
                    const treatmentItem = {
                        id: uuidv4(),
                        itemId: treatment.id,
                        description: `${treatment.name || 'Treatment'} - OD`,
                        type: ButtonType.TREATMENTS,
                        quantity: 1,
                        price: +treatment.listPrice || 0,
                        tax: treatment.defaultTaxPercentage || 0,
                        discount: 0,
                        total: 0,
                        date: getCurrentUTCDate(),
                        groupId: groupId,
                        eye: 'OD'
                    };
                    actions.addItem(treatmentItem);
                });
            }

            if (lens && lens.OS && lens.OS.treatments && lens.OS.treatments.length > 0) {
                lens.OS.treatments.forEach(treatment => {
                    const treatmentItem = {
                        id: uuidv4(),
                        itemId: treatment.id,
                        description: `${treatment.name || 'Treatment'} - OS`,
                        type: ButtonType.TREATMENTS,
                        quantity: 1,
                        price: +treatment.listPrice || 0,
                        tax: treatment.defaultTaxPercentage || 0,
                        discount: 0,
                        total: 0,
                        date: getCurrentUTCDate(),
                        groupId: groupId,
                        eye: 'OS'
                    };
                    actions.addItem(treatmentItem);
                });
            }

            // Add standalone treatments if any (from old workflow)
            if (treatments && treatments.length > 0) {
                treatments.forEach(treatment => {
                    const treatmentItem = {
                        id: uuidv4(),
                        itemId: treatment.id,
                        description: treatment.name || 'Treatment',
                        type: ButtonType.TREATMENTS,
                        quantity: 1,
                        price: +treatment.listPrice || 0,
                        tax: treatment.defaultTaxPercentage || 0,
                        discount: 0,
                        total: 0,
                        date: getCurrentUTCDate(),
                        groupId: groupId
                    };
                    actions.addItem(treatmentItem);
                });
            }

            // Create glasses order metadata
            // const glassesOrder = {
            //     groupId: item.groupId,
            //     rxId: item.selectedRx?.id,
            //     rx: item.selectedRx,
            //     labId: item.selectedLab?.id,
            //     labInstructions: item.labNotes,
            //     lab: item.selectedLab,
            //     orderDate: item.orderDate,
            //     promiseDate: item.promiseDate,
            //     rushOrder: item.rushOrder || false,
            //     frameHandling: item.frameHandling,
            //     orderType: 'glasses'
            // };

            // Store glasses order for backend processing
            actions.setInvoiceMeta({
                //glassesOrdersForLab: [...(state.glassesOrdersForLab || []), glassesOrder],
                selectService: false,
                selectTreatment: false,
                selectAccessory: false,
                selectGlasses: false
            });
        } else if (type === ButtonType.CONTACT_LENS) {
           const { od, os, groupId, rx } = item;
            
            // Add Right Eye (OD) configured contact lens if it exists
            if (od && od.contactLensModelId) {
                const odItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OD',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: od.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox
                    }
                };
                actions.addItem(odItem);
            }

            // Add Left Eye (OS) configured contact lens if it exists
            if (os && os.contactLensModelId) {
                const osItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OS',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: os.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox
                    }
                };
                actions.addItem(osItem);
            }

            // Close the dialog after adding items
            actions.setInvoiceMeta({
                selectConfiguredContactLens: false
            });
        } else if (type === ButtonType.FRAME) {
            // Add frame to invoice
            const frameItem = {
                id: uuidv4(), // Generate a unique ID for this instance in the UI state
                itemId: item.itemId || null, // Inventory item ID (null for patient own frames)
                modelNumber: item.modelNumber || null,
                brand: item.brand || null,
                description:  item.modelNumber,
                type: ButtonType.FRAME,
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
                groupId: null,
                frameHandling: 'stock',
                customerSuppliedFrame: null,
                properties:  null
            };
            actions.addItem(frameItem);
        } else {
            // take description from name or modelNumber
            const newItem = {
                // Assign a unique ID for this instance in the UI. Generate a temporary UUID.            
                id: uuidv4(),
                itemId: item.id,
                description: item.name || item.modelNumber || item.description || 'New Item', // Use description from item if available
                type: type,
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
            };

            // Dispatch an action to ADD_ITEM
            actions.addItem(newItem );
            actions.setInvoiceMeta({ selectService: false, selectTreatment: false, selectAccessory: false, selectGlasses: false });
        }
    }

    const handleTypeClick = (type) => {
        switch (type) {
            case 'frame':
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            case 'SEND_TO_LAB':
                actions.setInvoiceMeta({ labOrderDialog: true });
                break;
            default:
                break;
        }
    };

    const deletePayment = (paymentId) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // Always use markPaymentForDeletion - it will handle both temp and persisted payments
                actions.markPaymentForDeletion(paymentId);
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark payment for deletion? It will be removed when you save the invoice.`,
        });
    }

    // Refund handlers
    const handleAddRefund = (refundData) => {
        // Generate temporary ID for UI tracking
        const tempId = Date.now();
        const tempRefund = { 
            ...refundData, 
            tempId, 
            status: 'pending',
            invoiceId: state.id 
        };
        
        // Just add to state - will be saved when user clicks Save
        actions.addRefund(tempRefund);
        actions.setInvoiceMeta({ status: 'refund' });
    };

    const handleEditRefund = (refundData) => {
        // Just update state - will be saved when user clicks Save
        actions.updateRefund(refundData);
    };

    const handleUpdateRefundStatus = async (refundId, status) => {
        try {
            const response = await InvoiceService.updateRefundStatus(refundId, status);
            const { code, message } = response;

            if (code === 0) {
                actions.setInvoiceMeta({ type: 'UPDATE_REFUND_STATUS', refundId, status });
                toast.success(message);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error('Error updating refund status:', error);
            toast.error('Error updating refund status');
        }
    };

    const handleDeleteRefund = (refundId) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // Always use markRefundForDeletion - it handles both temp and persisted
                actions.markRefundForDeletion(refundId);
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark refund for deletion? It will be removed when you save the invoice.`,
        });
    };

    // Credit Memo handlers
    const handleAddCreditMemo = async (creditMemoData) => {
        try {
            // Validate there's money available to credit
            if (maxRefundAmount <= 0) {
                toast.error('No funds available to credit. Customer must have paid amount exceeding refunds.');
                return;
            }

            // Validate credit memo amount doesn't exceed available credit
            if (creditMemoData.amount > maxRefundAmount) {
                toast.error(`Credit memo amount ($${creditMemoData.amount.toFixed(2)}) exceeds maximum available credit ($${maxRefundAmount.toFixed(2)})`);
                return;
            }
            
            const tempId = Date.now();
            const tempCreditMemo = { 
                ...creditMemoData, 
                tempId, 
                status: 'approved',
                invoiceId: state.id 
            };
            
            // Use the proper action to add credit memo to state
            actions.addCreditMemo(tempCreditMemo);
        } catch (error) {
            console.error('Error creating credit memo:', error);
            toast.error('Error creating credit memo');
        }
    };

    const handleEditCreditMemo = async (creditMemoData) => {
        try {
            // Note: Credit memo editing may be limited based on status
            // Use the proper action to update credit memo in state
            actions.updateCreditMemo(creditMemoData);
        } catch (error) {
            console.error('Error updating credit memo:', error);
            toast.error('Error updating credit memo');
        }
    };

    const handleUpdateCreditMemoStatus = async (creditMemoId, status) => {
        try {
            const response = await CreditMemoService.updateCreditMemoStatus(creditMemoId, status);
            const { code, message } = response;

            if (code === 0) {
                actions.setInvoiceMeta({
                    type: 'UPDATE_CREDIT_MEMO_STATUS',
                    creditMemoId,
                    status
                });
                toast.success(message);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error('Error updating credit memo status:', error);
            toast.error('Error updating credit memo status');
        }
    };

    const handleCancelCreditMemo = async (creditMemoId, reason) => {

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const creditMemo = state.creditMemos.find(c => c.id === creditMemoId || c.tempId === creditMemoId);
                
                // If it's a temp credit memo (not yet saved), just remove it from state
                if (creditMemo && creditMemo.tempId && !creditMemo.id) {
                    actions.markCreditMemoForDeletion(creditMemoId);
                    closeConfirmationDialog();
                    return;
                }
                
                // If it's a persisted credit memo, call backend to cancel
                const response = await CreditMemoService.cancelCreditMemo(creditMemoId, reason);
                const { code, message, data } = response;

                if (code === 0) {
                    // Backend deleted it, so remove from UI state immediately
                    actions.removeCreditMemo(creditMemoId);
                    toast.success(message);
                } else {
                    toast.error(message);
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Delete credit memo record? This action cannot be undone.`,
        });
    };

    // --- Summary Calculations ---

    // This represents the sum of the base prices of all items before any item-level taxes or discounts are aggregated.
    //const lineItemsSubtotal = useMemo(() => sumMoney(state.invoiceItems.map(item => (item.quantity || 0) * (item.price || 0))), [state.invoiceItems]); // Recalculate when invoiceItems change

    // Calculate Total Item Tax Amount
    // const totalItemTaxAmount = useMemo(() => {
    //     return state.invoiceItems.reduce((sum, item) => {
    //         return sum + calculateItemTaxAmount(item);
    //     }, 0);
    // }, [state.invoiceItems]); // Recalculate when invoiceItems change

    //const totalItemTaxAmount = useMemo(() => sumMoney(state.invoiceItems.map(calculateItemTaxAmount)), [state.invoiceItems]); // Recalculate when invoiceItems change

    // Calculate Total Item Discount Amount
    // const totalItemDiscountAmount = useMemo(() => {
    //     return state.invoiceItems.reduce((sum, item) => {
    //         return sum + calculateItemDiscountAmount(item);
    //     }, 0);
    // }, [state.invoiceItems]); // Recalculate when invoiceItems change

    //const totalItemDiscountAmount = useMemo(() => sumMoney(state.invoiceItems.map(calculateItemDiscountAmount)), [state.invoiceItems]); // Recalculate when invoiceItems change

    // Calculate Total Overall Discount Amount from Discounts/Promotions
    // const totalOverallDiscountAmount = useMemo(() => { return calculateTotalOverallDiscountAmount(state.discounts, lineItemsSubtotal); }, [state.discounts, lineItemsSubtotal]); // Recalculate when discounts or lineItemsSubtotal change

    // Calculate Total Discount (Item Discounts + Overall Discounts)
    //const totalDiscount = totalItemDiscountAmount + totalOverallDiscountAmount;

    // Calculate Discounted Subtotal (apply all discounts before tax)
    //const discountedSubtotal = lineItemsSubtotal - totalDiscount;

    // Calculate Tax on Discounted Amount
    /*const taxOnDiscountedAmount = useMemo(() => {
        return sumMoney(state.invoiceItems.map(item => {
            const itemSubtotal = (item.quantity || 1) * (item.price || 0);
            const itemDiscountAmount = (item.discount || 0) / 100 * itemSubtotal;
            const itemShare = itemSubtotal > 0 ? itemSubtotal / lineItemsSubtotal : 0;
            const itemOverallDiscount = totalOverallDiscountAmount * itemShare;
            const itemDiscountedAmount = itemSubtotal - itemDiscountAmount - itemOverallDiscount;
            return (item.tax || 0) / 100 * Math.max(0, itemDiscountedAmount);
        }));
    }, [state.invoiceItems, lineItemsSubtotal, totalOverallDiscountAmount]);*/

    // Calculate Total Due (Discounted Subtotal + Tax on Discounted Amount)
    //const totalDue = useMemo(() => { return discountedSubtotal + taxOnDiscountedAmount; }, [discountedSubtotal, taxOnDiscountedAmount]);

    // Calculate Total Customer Paid (from payments)
    // const totalCustomerPaid = useMemo(() => sumMoney(state.payments.map(payment => payment.paymentAmount || 0)), [state.payments]); // Recalculate when payments change

    // Calculate Total Insurance Approved (from claims with Approved/Paid status)
    /* const totalInsuranceApproved = useMemo(() => {
        return state.claims.reduce((sum, claim) => {
            // Only sum approved or paid claims
            if (claim.status === 'Approved' || claim.status === 'Paid') {
                return sum + (claim.approvedAmount || 0);
            }
            return sum;
        }, 0);
    }, [state.claims]); */// Recalculate when claims change

    // Calculate Total Refunded (from approved/processed refunds)
    /*const totalRefunded = useMemo(() => {
        return sumMoney(state.refunds
            .filter(refund => refund.status === 'approved' || refund.status === 'processed')
            .map(refund => refund.refundAmount || 0));
    }, [state.refunds]); */

    // Calculate Total Store Credit Available (from approved credit memos not yet applied)
    // const creditMemos = useMemo(() => {
    //     return state.payments.map(payment => payment.creditMemos).flat().filter(memo => memo.status === 'approved');
    // }, [state.payments]);
    // const totalStoreCredit = useMemo(() => {
    //     return creditMemos.reduce((sum, memo) => sum + parseFloat(memo.amountToApply), 0);
    // }, [creditMemos]);

    // Calculate Maximum Refundable Amount (Total Paid - Total Refunded)
    //const maxRefundAmount = useMemo(() => { return Math.max(0, totalCustomerPaid - totalRefunded); }, [totalCustomerPaid, totalRefunded]);

    // Calculate Total Collected (Customer Payments + Insurance Approved)
    // const totalCollected = totalCustomerPaid + totalInsuranceApproved

    // Calculate Balance Due (Total Due - Total Collected - Total Refunded - Total Credit Memos)
    // Note: Refunds reduce the amount owed, so they should be subtracted
    //const balanceDue = truncateToTwoDecimals(totalDue) - truncateToTwoDecimals(totalCollected) - truncateToTwoDecimals(totalRefunded) //- truncateToTwoDecimals(totalStoreCredit);


    // --- End Summary Calculations ---

    return (
        
        <Dialog 
            onClose={onClose} 
            open={open} 
            fullScreen 
            maxWidth="xl" 
            fullWidth 
            disableScrollLock
            scroll="paper"
        >
            <AppDialogTitle onClose={onClose} title={`Edit ${state.status === 'quote' ? 'Quote' : 'Invoice'} - ${state.status.toUpperCase()}`} />

            <DialogContent
                sx={{
                    // Critical: Explicitly enable vertical scrolling to fix Safari scroll issues
                    // Safari sometimes fails to recalculate scroll dimensions when async content loads
                    overflowY: 'auto',
                    
                    // Enable momentum/smooth scrolling on iOS and Safari
                    WebkitOverflowScrolling: 'touch',
                    
                    // Establish positioning context for the scroll container
                    position: 'relative',
                    
                    // Custom scrollbar styling for better visibility
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.3)',
                        borderRadius: 4,
                    },
                }}
            >
                {wasClosedInitially && (
                    <Box sx={{ p: 2, mb: 2, backgroundColor: 'warning.light', color: 'warning.contrastText', textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="h6">This invoice is Closed/Refunded and cannot be edited.</Typography>
                    </Box>
                )}

                <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                    <CustomerInfobar customerData={customer} />
                </Box>

                {/* not needed Transfer Trail - Shows the chain of invoice transfers */}

                <Box display={'flex'} alignItems="center" justifyContent='space-between' sx={{ mb: 1, mt: 1 }}>

                    <Box>
                        <Typography sx={{ pr: 1 }}>Invoice date: {niceDate(state.invoiceDate)}</Typography>
                        {
                            state.invoiceNumber && (<Typography sx={{ pr: 2, fontWeight: 'bold' }}>Invoice #: {state.invoiceNumber}</Typography>
                            )
                        }
                    </Box>


                    <Box display={'flex'} alignItems="center">
                        <TextField label="Tray #" labelid="tray" size="small" sx={{ pr: 1 }} value={state.tray}
                            onChange={handleTrayChange} error={!!state.errors.tray}
                            helperText={state.errors.tray} disabled={wasClosedInitially}></TextField>
                        <TextField label="Associate" labelid="associate" size="small" sx={{ pr: 1 }}
                            value={state.associate} error={!!state.errors.associate}
                            helperText={state.errors.associate} onChange={handleAssociateChange} disabled={wasClosedInitially}></TextField>
                        <SelectLocation location={state.location ? +state.location.id : ''} setLocation={(location) => actions.setInvoiceMeta({ location: location })} error={!!state.errors.location} helperText={state.errors.location} disabled={wasClosedInitially} />
                    </Box>

                </Box>

                {/* buttons for new order, treatment, etc */}
                <InvoiceButtonComponent onButtonClick={handleButtonClick} disabled={wasClosedInitially} />
                <Divider sx={{ mt: 1 }} />

                {/* table to show invoice items */}
                {
                    invoiceItemsLoading ? <Typography>Loading invoice items...</Typography> :
                        <InvoiceItemsTable
                            invoiceItems={state.invoiceItems}
                            onEditItem={handleEditItem}
                            onDeleteItem={handleDeleteItem}
                            onItemUpdate={handleItemUpdate}
                            disabled={wasClosedInitially}
                        />
                }

                {/* a grid to show payment and invoice summary like total, tax, etc */}
                <Grid container spacing={1} sx={{ mt: 0.5 }}>

                    <Grid item xs={12} md={6}>
                        {/* Discounts & Promotions Component */}
                        {
                            invoiceItemsLoading ? <Typography>Loading discounts & promotions</Typography> :
                                <DiscountPromotion
                                    discounts={state.discounts}
                                    orderSubtotal={lineItemsSubtotal} // Pass subtotal for calculation display
                                    onAddDiscount={(discount) => actions.addDiscount(discount)}
                                    onEditDiscount={(discount) => actions.updateDiscount(discount)}
                                    onDeleteDiscount={(discountId) => {
                                        showConfirmationDialog((action) => {
                                            if (action === validLookup.confirmationDialogAction.ok) {
                                                // Always use markDiscountForDeletion - it handles both temp and persisted
                                                actions.markDiscountForDeletion(discountId);
                                            }
                                            closeConfirmationDialog();
                                        }, {
                                            title: 'Mark for deletion',
                                            message: `Mark discount for deletion? It will be removed when you save the invoice.`,
                                        });
                                    }}
                                    disabled={wasClosedInitially}
                                />
                        }
                        {/* Insurance Claims Component */}
                        {
                            invoiceItemsLoading ? <Typography>Loading insurance claims...</Typography> :
                                <InsuranceClaimCollector
                                    claims={state.claims}
                                    customer={customer}
                                    amountToClaim={totalDue} // Pass the total due amount for claim calculation
                                    onAddClaim={(claim) => actions.addClaim( claim )}
                                    onEditClaim={(claim) => actions.updateClaim( claim )}
                                    onDeleteClaim={(claimId) => {
                                        showConfirmationDialog((action) => {
                                            if (action === validLookup.confirmationDialogAction.ok) {
                                                // Always use markClaimForDeletion - it handles both temp and persisted
                                                actions.markClaimForDeletion(claimId);
                                            }
                                            closeConfirmationDialog();
                                        }, {
                                            title: 'Mark for deletion',
                                            message: `Mark insurance claim for deletion? It will be removed when you save the invoice.`,
                                        });
                                    }}
                                    disabled={wasClosedInitially || isQuote}
                                />
                        }
                        {/* Payment Collector Component */}
                        {
                            invoiceItemsLoading ? <Typography>Loading payments...</Typography> :
                                <PaymentCollector
                                    payments={state.payments}
                                    onAddPayment={(payment) => actions.addPayment( payment )}
                                    onEditPayment={(payment) => actions.updatePayment( payment )}
                                    onDeletePayment={deletePayment}
                                    disabled={wasClosedInitially || isQuote}
                                    customerId={customer.customer.id}
                                    invoiceId={state.id}
                                    invoiceBalance={balanceDue}
                                    onRefresh={onRefresh}
                                />
                        }
                        {/* Credit Memo Collector Component */}
                        {
                            invoiceItemsLoading ? <Typography>Loading credit memos...</Typography> :
                                <CreditMemoCollector
                                    creditMemos={state.creditMemos}
                                    onAddCreditMemo={handleAddCreditMemo}
                                    onEditCreditMemo={handleEditCreditMemo}
                                    onCancelCreditMemo={handleCancelCreditMemo}
                                    onUpdateCreditMemoStatus={handleUpdateCreditMemoStatus}
                                    disabled={wasClosedInitially || isQuote}
                                    maxCreditAmount={maxRefundAmount}
                                    invoiceData={invoiceData}
                                />
                        }
                        {/* Refund Collector Component */}
                        {
                            invoiceItemsLoading ? <Typography>Loading refunds...</Typography> :
                                <RefundCollector
                                    refunds={state.refunds}
                                    onAddRefund={handleAddRefund}
                                    onEditRefund={handleEditRefund}
                                    onDeleteRefund={handleDeleteRefund}
                                    onUpdateRefundStatus={handleUpdateRefundStatus}
                                    disabled={wasClosedInitially || isQuote}
                                    payments={state.payments}
                                    maxRefundAmount={maxRefundAmount}
                                />
                        }
                    </Grid>
                    <Grid item xs={12} md={6} mt={1}>
                        <TextField
                            value={state.notes}
                            onChange={(e) => actions.setInvoiceMeta({ notes: e.target.value })}
                            name="notes"
                            label="Notes"
                            multiline
                            rows={4}
                            fullWidth
                            disabled={wasClosedInitially}
                        />
                        <Box sx={{ mt: 1, p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Subtotal:</Typography>
                                <Typography>{formatAmountForDisplay(lineItemsSubtotal)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Discount:</Typography>
                                <Typography>-{formatAmountForDisplay(totalDiscount)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Tax:</Typography>
                                <Typography>{formatAmountForDisplay(taxOnDiscountedAmount)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6">Total Due:</Typography>
                                <Typography variant="h6">{formatAmountForDisplay(totalDue)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography>Total Customer Paid:</Typography>
                                    <Typography variant="caption">Or Credit memo:</Typography>
                                </Box>
                                <Typography>{formatAmountForDisplay(totalCustomerPaid)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Insurance Approved:</Typography> {/* New label */}
                                <Typography>{formatAmountForDisplay(totalInsuranceApproved)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Refunded:</Typography>
                                <Typography sx={{ color: 'error.main' }}>-{formatAmountForDisplay(totalRefunded)}</Typography>
                            </Box>
                            {/* {totalStoreCredit > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography>Available Store Credit:</Typography>
                                    <Typography sx={{ color: 'primary.main' }}>{formatAmountForDisplay(totalStoreCredit)}</Typography>
                                </Box>
                            )} */}
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Balance:</Typography>
                                {/* Highlight Balance */}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: balanceDue > 0 ? 'error.main' : (balanceDue < 0 ? 'success.main' : 'text.primary')
                                    }}
                                >
                                    {formatAmountForDisplay(balanceDue)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                   
            </DialogContent >
            <DialogActions sx={{ p: 2, boxShadow: 8 }}>
                {
                    (state.errors.tray || state.errors.location || state.errors.associate) && <Typography color="error" sx={{ pr: 1 }}>Please fix issues before submitting</Typography>
                }
                {
                    state.inProgress ? <Typography>Saving...</Typography> :
                        (
                            <>
                                {/* Status Dropdown */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InvoiceStatusSelect statusTypes={invoiceStatusType} isLoading={invoivceStatusTypeLoading} selectedStatus={state.status} onStatusChange={handleStatusChange} disabled={wasClosedInitially} />

                                    {isQuote && (
                                        <Box onClick={handleTypeClick} sx={{ cursor: 'pointer' }}>
                                            <Chip
                                                icon={<InfoIcon />}
                                                label="Quote Mode"
                                                color="info"
                                                size="small"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {state.invoiceItems.length > 0 && (
                                    <>
                                        {state.status === 'quote' && (
                                            <Button
                                                onClick={handleConvertToInvoice}
                                                color="success"
                                                variant="contained"
                                                disabled={wasClosedInitially || state.inProgress}
                                                sx={{ mr: 1 }}
                                            >
                                                Convert to Invoice
                                            </Button>
                                        )}

                                        {/* Transfer Invoice Button - show for invoices that can be transferred */}
                                        <Can section='invoice' action='transfer-invoice'>
                                            {state.status !== 'transferred' &&
                                                !['cancelled', 'void', 'refund'].includes(state.status) && (
                                                    <Button
                                                        onClick={handleTransferInvoice}
                                                        color="warning"
                                                        variant="outlined"
                                                        disabled={wasClosedInitially || state.inProgress}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Transfer Invoice
                                                    </Button>
                                                )}
                                        </Can>
                                        <Can section='invoice' action='send-text'>
                                            <Button
                                                onClick={handleSendTextNotification}
                                                color="info"
                                                variant="outlined"
                                                disabled={wasClosedInitially || !customer.customer.mobile || !state.invoiceNumber}
                                                sx={{ mr: 1, }}
                                            >
                                                Send Pickup Text
                                            </Button>
                                        </Can>
                                        {/* <Button
                                            onClick={handleTestPrintInvoice}
                                            color="warning"
                                            variant="outlined"
                                            startIcon={<PrintIcon />}
                                            sx={{ mr: 1 }}
                                        >
                                            Test Print (Simple HTML)
                                        </Button> */}
                                        <Can section='invoice' action='print-invoice'>
                                            <Button
                                                onClick={handlePrintInvoice}
                                                color="secondary"
                                                variant="outlined"
                                                startIcon={isPrinting ? undefined : <PrintIcon />}
                                                disabled={!state.invoiceNumber || isPrinting}
                                                sx={{ mr: 1 }}
                                            >
                                                {isPrinting ? <CircularProgress size={20} /> : 'Print Invoice'}
                                            </Button>
                                        </Can>

                                        <Button
                                            onClick={handleAddTask}
                                            color="info"
                                            variant="outlined"
                                            startIcon={<PlaylistAddCheckIcon />}
                                        >
                                            Add Task
                                        </Button> 
                                        <Can section='invoice' action='edit-invoice'>                                       
                                            <Button sx={{ ml: 2 }} color="primary" variant="contained" type="button" onClick={async () => { await handleSave(); onClose(); }} autoFocus disabled={wasClosedInitially}>
                                                Save & Close
                                            </Button>
                                            <Button sx={{ ml: 1 }} color="primary" variant="outlined" type="button" onClick={handleSave} disabled={wasClosedInitially}>
                                                Save
                                            </Button>
                                        </Can>
                                    </>
                                )}
                                <Button color="cancel" variant="outlined" onClick={onClose}>Cancel</Button>
                            </>

                        )
                }
            </DialogActions>
            {/* Use NewGlassesDialog for both add and edit - it supports editing via existingGlassesData prop */}
            <NewGlassesDialog 
                open={state.editGlasses} 
                onClose={() => {
                    actions.setInvoiceMeta({ editGlasses: false, glassesToEdit: null });
                }} 
                onSelect={handleUpdateGlasses}
                customer={customer}
                existingGlassesData={state.glassesToEdit}
            />
            {/* <EditContactsDialog
                open={state.editContacts}
                onClose={() => {
                    actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null });
                }}
                contacts={state.contactsToEdit}
                onUpdate={handleUpdateContacts}
                onDeleteItem={handleDeleteItem}
                invoiceNumber={state.invoiceNumber}
                customer={customer}
            /> */}
            <NewGlassesDialog open={state.selectGlasses} 
            onClose={() => actions.setInvoiceMeta({ selectGlasses: false })} 
            onSelect={(glasses) => addToInvoice(ButtonType.NEW_GLASSES, glasses)} customer={customer} />

            {/* For new contact lens */}
            <ContactLensConfigurationDialog 
                open={state.selectConfiguredContactLens} 
                onClose={() => actions.setInvoiceMeta({ selectConfiguredContactLens: false })} 
                customer={customer}
                onSave={(contactLensData) => {
                    // Add groupId to the data
                    const dataWithGroupId = {
                        ...contactLensData,
                        groupId: uuidv4()
                    };
                    addToInvoice(ButtonType.CONTACT_LENS, dataWithGroupId);
                }} 
            />            
             {/* For editing a contact lens */}
            <ContactLensConfigurationDialog 
                open={state.editContacts} 
                onClose={() => actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null })} 
                customer={customer}
                onSave={handleUpdateContacts} 
                existingContactLensData={state.contactsToEdit}
            />
            <ServiceSelectDialog open={state.selectService} onClose={() => actions.setInvoiceMeta({ selectService: false })} onSelect={(service) => addToInvoice(ButtonType.SERVICES, service)} />
            {/* <LabOrderDialog
                open={state.labOrderDialog}
                onClose={() => actions.setInvoiceMeta({ labOrderDialog: false })}
                onSave={async (labOrderData) => {
                    try {
                        const labOrderToSave = {
                            ...labOrderData,
                            invoiceId: state.id,
                            groupId: uuidv4(),
                            orderType: 'glasses',
                        };

                        const response = await LabOrderService.saveLabOrder(labOrderToSave);
                        const { code, message } = response;

                        if (code === 0) {
                            toast.success(message || 'Lab order saved successfully');
                            actions.setInvoiceMeta({
                                labOrderDialog: false,
                                glassesOrdersForLab: [...state.glassesOrdersForLab, labOrderToSave]
                            });
                        } else {
                            toast.error(message || 'Failed to save lab order');
                        }
                    } catch (error) {
                        toast.error('Error saving lab order');
                        console.error('Lab order save error:', error);
                    }
                }}
            /> */}
            <TreatmentSelectDialog open={state.selectTreatment} onClose={() => actions.setInvoiceMeta({ selectTreatment: false })} onSelect={(treatment) => addToInvoice(ButtonType.TREATMENTS, treatment)} />
            <AccessorySelectDialog open={state.selectAccessory} onClose={() => actions.setInvoiceMeta({ selectAccessory: false })} onSelect={(accessory) => addToInvoice(ButtonType.ACCESSORIES, accessory)} />
            <FrameSelectDialog open={state.selectFrame} onClose={() => actions.setInvoiceMeta({ selectFrame: false })} onSelect={(frame) => addToInvoice(ButtonType.FRAME, frame)} />
            <FrameSelectDialog open={state.editFrame} onClose={() => actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null })} onSelect={handleReplaceFrame} />                
            {/* LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow */}

            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => actions.setInvoiceMeta({ showTaskDialog: false })}
                entityType="invoice"
                entityId={state.invoiceNumber}
                onTaskCreated={() => actions.setInvoiceMeta({ showTaskDialog: false })}
            />

            <TransferInvoiceDialog
                open={state.showTransferDialog}
                onClose={() => actions.setInvoiceMeta({ showTransferDialog: false })}
                invoiceData={invoiceData}
                onTransferSuccess={handleTransferSuccess}
                onRefresh={onRefresh}
                hasPayments={state.payments && state.payments.length > 0} // Pass whether original invoice has payments
                hasDiscounts={state.discounts && state.discounts.length > 0} // Pass whether original invoice has discounts
                hasInsurance={state.claims && state.claims.length > 0} // Pass whether original invoice has insurance claims
            />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Dialog >
    )
}